import { useEffect, useRef, useState } from "react";

import api, { handleApi } from "pages/api/api";
import { useAlert } from "pages/context/AlertContext";
import { useAuth } from "pages/context/AuthContext";

import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";

import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import InputBase from "@mui/material/InputBase";
import CircularProgress from "@mui/material/CircularProgress";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Popper from "@mui/material/Popper";
import Divider from "@mui/material/Divider";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import MenuSearchMateriais from "./MenuSearchMateriais";
import MenuSearchEntidades from "./MenuSearchEntidades";

const SIZES = {
  sm: { fontSize: 12, px: 1, py: 0.25, width: 220, radius: 1 },
  md: { fontSize: 13.5, px: 1.2, py: 0.55, width: 260, radius: 1.5 },
  lg: { fontSize: 15, px: 1.4, py: 0.8, width: 300, radius: 2 },
};

export default function MenuSearch({
  placeholder = "Procure aqui",
  maxSuggestions = 100,
  size = "sm",
}) {
  const cfg = SIZES[size] ?? SIZES.sm;

  const [imgsByRef, setImgsByRef] = useState({});

  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const [showMenus, setShowMenus] = useState(true);
  const [showEntidades, setShowEntidades] = useState(true);
  const [showMercadorias, setShowMercadorias] = useState(true);
  const [showOutrosMateriais, setShowOutrosMateriais] = useState(true);

  const [stocksOpen, setStocksOpen] = useState(false);
  const [selMaterial, setSelMaterial] = useState(null);

  const [entOpen, setEntOpen] = useState(false);
  const [selEntidade, setSelEntidade] = useState(null);

  const theme = useTheme();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { userData, temFuncao } = useAuth();

  const anchorRef = useRef(null);
  const debouncedQ = useDebounce(q, 220);

  // -------------------- fetch sugestões --------------------
  useEffect(() => {
    let active = true;

    const fetchIt = async () => {
      if (!debouncedQ) {
        setItems([]);
        setOpen(false);
        return;
      }

      setLoading(true);

      const [data, err] = await handleApi(
        api.post("Pesquisa/QuickSearch", {
          q: debouncedQ,
          limit: maxSuggestions,
          refvendedor: temFuncao("Comercial") ? userData?.refvendedor || "" : "",
        })
      );

      if (!active) return;

      setLoading(false);

      if (err) {
        showAlert(err, "error");
        setItems([]);
        setOpen(false);
        return;
      }

      const list = Array.isArray(data) ? data : [];
      setItems(list);
      setOpen(list.length > 0);
      setHighlight(-1);

      carregarImagensSugestoes(list);
    };

    fetchIt();

    return () => {
      active = false;
    };
  }, [debouncedQ, maxSuggestions, showAlert]);

  const carregarImagensSugestoes = async (listaItems) => {
    const refs = Array.from(
      new Set(
        (listaItems || [])
          .filter((it) => (it?.kind || "").toLowerCase() === "material")
          .map((it) => String(it?.ref || it?.refmaterial || "").trim())
          .filter(Boolean)
      )
    );

    // só pedir refs que ainda não temos
    const faltam = refs.filter((r) => !imgsByRef[r]);
    if (faltam.length === 0) return;

    const [dados, err] = await handleApi(
      api.post("Materiais/SelMateriaisImagens", { refs: faltam })
    );

    if (err) return;

    const mapa = {};
    (Array.isArray(dados) ? dados : []).forEach((x) => {
      const ref = String(x?.refmaterial || "").trim();
      const url = String(x?.url || "").trim();
      if (ref && url) mapa[ref] = url;
    });

    setImgsByRef((prev) => ({ ...(prev || {}), ...mapa }));
  };

  const filteredItems = (items || []).filter((it) => {
    const kind = String(it?.kind || "").toLowerCase();

    if (kind === "menu") return showMenus;
    if (kind === "entidade") return showEntidades;

    if (kind === "material") {
      const isFert = Number(it?.isfert ?? 0) === 1;
      return isFert ? showMercadorias : showOutrosMateriais;
    }

    return false;
  });

  // -------------------- ações --------------------

  const limparPesquisa = () => {
    setOpen(false);
    setQ("");
    setItems([]);
    setHighlight(-1);
  };

  const goToRoute = (route) => {
    limparPesquisa();
    if (route) navigate(route);
  };

  const handleItemClick = (item) => {
    if (!item) return;
    const kind = (item.kind || "").toLowerCase();

    if (kind === "menu") {
      goToRoute(item.route);
      return;
    }

    // fecha dropdown mas não limpa o texto
    setOpen(false);
    setHighlight(-1);

    if (kind === "material") {
      const ref = String(item.ref ?? item.refmaterial ?? "").trim();
      setSelMaterial({
        ...item,
        urlImagem: imgsByRef?.[ref] || "",
      });
      setStocksOpen(true);
    } else if (kind === "entidade") {
      setSelEntidade(item);
      setEntOpen(true);
    }
  };

  const onKeyDown = (e) => {
    if (!open || filteredItems.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => (h + 1) % filteredItems.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => (h - 1 + filteredItems.length) % filteredItems.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const idx = highlight >= 0 ? highlight : 0;
      if (filteredItems[idx]) handleItemClick(filteredItems[idx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (highlight >= filteredItems.length) {
      setHighlight(filteredItems.length > 0 ? 0 : -1);
    }
  }, [filteredItems.length, highlight]);

  // -------------------- render --------------------
  return (
    <>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <div>
          <InputBase
            inputRef={anchorRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onFocus={() => q && items.length > 0 && setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            sx={{
              color: theme.palette.common.white,
              border: "1px solid rgba(255,255,255,0.5)",
              px: cfg.px,
              py: cfg.py,
              borderRadius: cfg.radius,
              width: cfg.width,
              fontSize: cfg.fontSize,
            }}
            inputProps={{ "aria-label": "Pesquisar" }}
            endAdornment={
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                {loading && <CircularProgress size={14} />}
                {(q || open) && (
                  <button
                    type="button"
                    onClick={limparPesquisa}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      border: "none",
                      padding: 0,
                      marginLeft: 4,
                      backgroundColor: "rgba(255,255,255,0.25)",
                      color: "#fff",
                      fontSize: 11,
                      lineHeight: "18px",
                      textAlign: "center",
                      cursor: "pointer",
                    }}
                    title="Limpar pesquisa"
                  >
                    ×
                  </button>
                )}
              </div>
            }
          />

          <Popper
            open={open && items.length > 0}
            anchorEl={anchorRef.current}
            placement="bottom-end"
            style={{ zIndex: 1600 }}
            modifiers={[
              { name: "offset", options: { offset: [0, 8] } },
              { name: "preventOverflow", options: { boundary: "viewport" } },
              { name: "flip", options: { fallbackPlacements: ["bottom-start", "top-end"] } },
            ]}
          >
            <Paper
              elevation={8}
              sx={{
                minWidth: 500,
                maxWidth: 640,
                maxHeight: 420,
                overflowY: "auto",
              }}
            >
              <div
                style={{
                  padding: "10px 12px 8px 12px",
                  background: "#fff",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    columnGap: 12,
                    rowGap: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showMenus}
                        onChange={(e) => {
                          setShowMenus(e.target.checked);
                          setHighlight(-1);
                        }}
                        size="small"
                      />
                    }
                    label="Menus"
                    sx={{ m: 0, ".MuiFormControlLabel-label": { fontSize: 12 } }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showEntidades}
                        onChange={(e) => {
                          setShowEntidades(e.target.checked);
                          setHighlight(-1);
                        }}
                        size="small"
                      />
                    }
                    label="Entidades"
                    sx={{ m: 0, ".MuiFormControlLabel-label": { fontSize: 12 } }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showMercadorias}
                        onChange={(e) => {
                          setShowMercadorias(e.target.checked);
                          setHighlight(-1);
                        }}
                        size="small"
                      />
                    }
                    label="Mercadorias"
                    sx={{ m: 0, ".MuiFormControlLabel-label": { fontSize: 12 } }}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showOutrosMateriais}
                        onChange={(e) => {
                          setShowOutrosMateriais(e.target.checked);
                          setHighlight(-1);
                        }}
                        size="small"
                      />
                    }
                    label="Outros materiais"
                    sx={{ m: 0, ".MuiFormControlLabel-label": { fontSize: 12 } }}
                  />
                </div>
              </div>

              <Divider />

              <List dense disablePadding>
                {filteredItems.map((it, idx) => {
                  const kind = (it.kind || "").toLowerCase();
                  let secondary = it.subtitle || "";

                  if (kind === "material") {
                    const isFert = Number(it?.isfert ?? 0) === 1;
                    secondary = `${isFert ? "Mercadoria" : "Outro material"} • ref ${it.ref ?? ""}`;
                  } else if (kind === "entidade") {
                    secondary = `Entidade • ref ${it.ref ?? ""}${it.nif ? ` • NIF ${it.nif}` : ""}`;
                  } else if (kind === "menu") {
                    secondary = `Menu${it.route ? ` • ${it.route}` : ""}`;
                  }

                  return (
                    <ListItemButton
                      key={`${kind}-${it.id ?? it.route ?? it.ref ?? idx}`}
                      selected={idx === highlight}
                      onMouseEnter={() => setHighlight(idx)}
                      onMouseLeave={() => setHighlight(-1)}
                      onClick={() => handleItemClick(it)}
                    >
                      <div
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%" }}
                      >
                        {kind === "material" ? (
                          (() => {
                            const ref = String(it.ref ?? it.refmaterial ?? "").trim();
                            const url = imgsByRef?.[ref] || "";
                            if (!url)
                              return <span style={{ display: "inline-block", width: 26 }} />;
                            return (
                              <img
                                src={url}
                                alt={ref}
                                title="Abrir imagem"
                                style={{
                                  width: 26,
                                  height: 26,
                                  objectFit: "contain",
                                  borderRadius: 6,
                                  border: "1px solid #eee",
                                  background: "#fff",
                                }}
                              />
                            );
                          })()
                        ) : (
                          <span style={{ display: "inline-block", width: 26 }} />
                        )}

                        <ListItemText primary={it.label || it.title} secondary={secondary} />
                      </div>
                    </ListItemButton>
                  );
                })}

                {filteredItems.length === 0 && (
                  <div
                    style={{
                      padding: "14px 16px",
                      fontSize: 12,
                      color: "#7b809a",
                    }}
                  >
                    Sem resultados para os filtros selecionados.
                  </div>
                )}
              </List>
            </Paper>
          </Popper>
        </div>
      </ClickAwayListener>

      {/* Dialog material */}
      <MenuSearchMateriais
        open={stocksOpen}
        onClose={() => setStocksOpen(false)}
        material={selMaterial}
      />

      {/* Dialog entidade */}
      <MenuSearchEntidades
        open={entOpen}
        onClose={() => setEntOpen(false)}
        entidade={selEntidade}
        maxRows={20}
      />
    </>
  );
}

// debounce local
function useDebounce(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}
